import api from './api';

export interface SimulationConfig {
  vehicleMode: 'auto' | 'manual';
  vehicleCount?: number;
  vehicleCapacity: number;
  minStopsPerRoute: number; // For auto mode: limits max vehicles to ensure balanced routes
  speedFactor: number;
  maxTravelTimeSeconds: number;
  startTime: string;
  endTime: string;
  breaksEnabled: boolean;
  breakDurationMinutes: number;
  optimizationGoal: 'balanced' | 'minimize_time' | 'minimize_vehicles';
  serviceTimePerParcel: number;
  baseServiceTimeMinutes: number;
  useMerchantTimeWindows: boolean;
  excludeDedicatedDriverMerchants: boolean; // If true, exclude merchants that require dedicated drivers
  hubStart: boolean;
  costPerHour: number;
  costPerKm: number;
  fixedVehicleCost: number;
}

export interface SimulationRouteStop {
  taskUid: string;
  taskType: 'pickup' | 'rto' | 'cash';
  sequence: number;
  location: {
    lat: number;
    lng: number;
  };
  arrivalTimestamp: number;
  serviceSeconds: number;
}

export interface ExcludedDedicatedTask {
  task_uid: string;
  merchant_id: number;
  merchant_name: string;
  lat: number;
  lng: number;
  parcel_count: number;
}

export interface SimulationRoute {
  driverUid: string;
  driverName: string;
  totalDistanceMeters: number;
  totalDurationSeconds: number;
  totalServiceSeconds: number;
  totalCost: number;
  stops: SimulationRouteStop[];
}

export interface SimulationVersoResponse {
  routes: SimulationRoute[];
  unassignedTaskUids: string[];
}

export interface SimulationResult {
  totalRoutes: number;
  totalStops: number;
  totalParcels: number;
  totalDistanceKm: number;
  totalDurationMinutes: number;
  totalCost: number;
  unassignedCount: number;
  unassignedTaskUids: string[];
  excludedDedicatedTasks?: ExcludedDedicatedTask[];
  excludedDedicatedCount?: number;
  routes: {
    routeIndex: number;
    stopCount: number;
    parcelCount: number;
    distanceKm: number;
    durationMinutes: number;
    cost: number;
  }[];
}

export interface Simulation {
  _id: string;
  name: string;
  description?: string;
  simulationDate: string;
  taskCount: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: SimulationResult;
  versoResponse?: SimulationVersoResponse;
  config: SimulationConfig;
  executionTimeMs?: number;
  errorMessage?: string;
  created_at: string;
}

export interface SimulationTask {
  task_uid: string;
  task_id: number;
  type: 'pickup' | 'rto' | 'cash';
  lat: number;
  lng: number;
  address: string;
  merchant_name: string | null;
  parcel_count: number;
  status: string;
  zone_id: string | null;
  zone_name: string | null;
  zone_color: string | null;
  has_time_window: boolean;
  time_window: { start: string; end: string } | null;
}

export interface SimulationZone {
  _id: string;
  name: string;
  city_id: number;
}

/**
 * Get tasks available for simulation on a given date
 */
export async function getSimulationTasks(
  date: string,
  zoneIds?: string[]
): Promise<{ date: string; taskCount: number; tasks: SimulationTask[] }> {
  const params = new URLSearchParams({ date });
  if (zoneIds && zoneIds.length > 0) {
    zoneIds.forEach(id => params.append('zoneIds', id));
  }
  const response = await api.get(`/simulations/tasks?${params.toString()}`);
  return response.data;
}

/**
 * Get available zones for filtering
 */
export async function getSimulationZones(): Promise<{ zones: SimulationZone[] }> {
  const response = await api.get('/simulations/zones');
  return response.data;
}

/**
 * Run a new simulation
 */
export async function runSimulation(params: {
  name: string;
  description?: string;
  date: string;
  taskUids: string[]; // Pass filtered task UIDs to run simulation only on these tasks
  config: SimulationConfig;
}): Promise<{ success: boolean; simulation: Simulation }> {
  const response = await api.post('/simulations/run', params);
  return response.data;
}

/**
 * Get simulation history
 */
export async function getSimulationHistory(params?: {
  limit?: number;
  offset?: number;
  date?: string;
}): Promise<{ simulations: Simulation[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());
  if (params?.date) searchParams.set('date', params.date);
  
  const response = await api.get(`/simulations?${searchParams.toString()}`);
  return response.data;
}

/**
 * Get a specific simulation
 */
export async function getSimulation(id: string): Promise<{ simulation: Simulation }> {
  const response = await api.get(`/simulations/${id}`);
  return response.data;
}

/**
 * Delete a simulation
 */
export async function deleteSimulation(id: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/simulations/${id}`);
  return response.data;
}

/**
 * Delete all simulations
 */
export async function deleteAllSimulations(): Promise<{ success: boolean; deletedCount: number }> {
  const response = await api.delete('/simulations');
  return response.data;
}
